import { useMemo, useState, type DragEvent } from "react";
import { Filter, Share2, Plus, ChevronLeft, ChevronRight, User2, GripVertical } from "lucide-react";

type Vue = "Jour" | "Semaine" | "Mois";

const DOCTORS = [
  { id: "d1", name: "Dr. Marie Dubois", spec: "Cardiologie", img: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=120&q=60" },
  { id: "d2", name: "Dr. Andrew Smith", spec: "Médecine générale", img: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=120&q=60" },
  { id: "d3", name: "Dr. Karim El Idrissi", spec: "Pédiatrie", img: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=120&q=60" },
  { id: "d4", name: "Inf. Sara Benali", spec: "Soins infirmiers", img: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=crop&w=120&q=60" },
  { id: "d5", name: "Dr. Tony Lambert", spec: "Dermatologie", img: "https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&w=120&q=60" },
];

type Status = "Confirmé" | "En cours" | "Bloqué" | "Terminé" | "Non démarré";

type Apt = {
  id: string;
  doctorId: string;
  patient: string;
  status: Status;
  ref: string;
  /** ISO date YYYY-MM-DD */
  date: string;
  /** "HH:00" slot key */
  slot: string;
  durationH: number;
};

const STATUS_TONE: Record<Status, string> = {
  Confirmé: "bg-[oklch(0.65_0.2_250)]/20 text-[oklch(0.78_0.18_250)] ring-1 ring-[oklch(0.65_0.2_250)]/40",
  "En cours": "bg-[oklch(0.85_0.18_85)]/20 text-[oklch(0.85_0.18_85)] ring-1 ring-[oklch(0.85_0.18_85)]/40",
  Bloqué: "bg-destructive/15 text-destructive ring-1 ring-destructive/40",
  Terminé: "bg-[oklch(0.78_0.22_140)]/20 text-[oklch(0.78_0.22_140)] ring-1 ring-[oklch(0.78_0.22_140)]/40",
  "Non démarré": "bg-white/5 text-muted-foreground ring-1 ring-white/10",
};

// 09:00 → 18:00, 1h slots
const HOURS = Array.from({ length: 10 }, (_, i) => `${String(9 + i).padStart(2, "0")}:00`);

function startOfWeek(d: Date) {
  const r = new Date(d);
  const day = (r.getDay() + 6) % 7; // Mon=0
  r.setDate(r.getDate() - day);
  r.setHours(0, 0, 0, 0);
  return r;
}
function fmtISO(d: Date) {
  return d.toISOString().slice(0, 10);
}
function addDays(d: Date, n: number) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function addMonths(d: Date, n: number) {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}

const SEED_TODAY = new Date();
const MONDAY = startOfWeek(SEED_TODAY);

const INITIAL: Apt[] = [
  { id: "a1", doctorId: "d1", patient: "Yassine Amrani", status: "Confirmé", ref: "RDV-22", date: fmtISO(MONDAY), slot: "09:00", durationH: 1 },
  { id: "a2", doctorId: "d1", patient: "Lina Bakkali", status: "En cours", ref: "RDV-23", date: fmtISO(MONDAY), slot: "11:00", durationH: 1 },
  { id: "a3", doctorId: "d2", patient: "Omar Saidi", status: "Terminé", ref: "RDV-18", date: fmtISO(addDays(MONDAY, 1)), slot: "10:00", durationH: 1 },
  { id: "a4", doctorId: "d2", patient: "Salma Idrissi", status: "Confirmé", ref: "RDV-19", date: fmtISO(addDays(MONDAY, 1)), slot: "14:00", durationH: 1 },
  { id: "a5", doctorId: "d3", patient: "Adam Tazi", status: "Bloqué", ref: "RDV-13", date: fmtISO(addDays(MONDAY, 2)), slot: "09:00", durationH: 1 },
  { id: "a6", doctorId: "d3", patient: "Nour Chami", status: "Confirmé", ref: "RDV-14", date: fmtISO(addDays(MONDAY, 2)), slot: "15:00", durationH: 1 },
  { id: "a7", doctorId: "d4", patient: "Karim Fassi", status: "En cours", ref: "RDV-11", date: fmtISO(addDays(MONDAY, 3)), slot: "10:00", durationH: 1 },
  { id: "a8", doctorId: "d5", patient: "Hiba Naciri", status: "Confirmé", ref: "RDV-16", date: fmtISO(addDays(MONDAY, 3)), slot: "16:00", durationH: 1 },
  { id: "a9", doctorId: "d1", patient: "Rania Berrada", status: "Non démarré", ref: "RDV-24", date: fmtISO(addDays(MONDAY, 4)), slot: "11:00", durationH: 1 },
  { id: "a10", doctorId: "d5", patient: "Mehdi Alaoui", status: "Confirmé", ref: "RDV-17", date: fmtISO(addDays(MONDAY, 4)), slot: "13:00", durationH: 1 },
];

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function AgendaView() {
  const [vue, setVue] = useState<Vue>("Semaine");
  const [cursor, setCursor] = useState<Date>(SEED_TODAY);
  const [doctorId, setDoctorId] = useState<string>("d1");
  const [appts, setAppts] = useState<Apt[]>(INITIAL);
  const [drag, setDrag] = useState<{ id: string; from: string } | null>(null);
  const [hover, setHover] = useState<string | null>(null);

  const visibleAppts = useMemo(() => appts.filter((a) => a.doctorId === doctorId), [appts, doctorId]);

  const move = (id: string, date: string, slot: string) => {
    setAppts((prev) => prev.map((a) => (a.id === id ? { ...a, date, slot } : a)));
  };

  const onDragStart = (e: DragEvent<HTMLDivElement>, a: Apt) => {
    setDrag({ id: a.id, from: `${a.date}|${a.slot}` });
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (e: DragEvent<HTMLDivElement>, date: string, slot: string) => {
    e.preventDefault();
    if (!drag) return;
    move(drag.id, date, slot);
    setDrag(null);
    setHover(null);
  };
  const allowDrop = (e: DragEvent<HTMLDivElement>, key: string) => {
    e.preventDefault();
    setHover(key);
  };

  // Range navigation
  const shift = (dir: -1 | 1) => {
    if (vue === "Jour") setCursor(addDays(cursor, dir));
    else if (vue === "Semaine") setCursor(addDays(cursor, dir * 7));
    else setCursor(addMonths(cursor, dir));
  };
  const today = () => setCursor(new Date());

  const rangeLabel = useMemo(() => {
    if (vue === "Jour") return cursor.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    if (vue === "Semaine") {
      const s = startOfWeek(cursor);
      const e = addDays(s, 6);
      return `${s.getDate()} – ${e.getDate()} ${e.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`;
    }
    return cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [vue, cursor]);

  const doctor = DOCTORS.find((d) => d.id === doctorId)!;

  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 rounded-full bg-white/5 p-1">
          {(["Jour", "Semaine", "Mois"] as Vue[]).map((v) => (
            <button
              key={v}
              onClick={() => setVue(v)}
              className={
                "rounded-full px-4 py-1.5 text-sm transition " +
                (vue === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")
              }
            >
              {v}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <button onClick={() => shift(-1)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-white/5">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={today} className="px-3 text-xs text-muted-foreground hover:text-foreground">
              Aujourd'hui
            </button>
            <button onClick={() => shift(1)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-white/5">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="hidden text-sm text-muted-foreground sm:block">{rangeLabel}</div>

          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm">
            <User2 className="h-4 w-4 text-primary" />
            <select
              value={doctorId}
              onChange={(e) => setDoctorId(e.target.value)}
              className="bg-transparent text-foreground outline-none"
            >
              {DOCTORS.map((d) => (
                <option key={d.id} value={d.id} className="bg-background">
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <button className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/5 text-muted-foreground hover:text-foreground">
            <Filter className="h-4 w-4" />
          </button>
          <button className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/5 text-muted-foreground hover:text-foreground">
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Selected doctor banner */}
      <div className="mt-5 flex items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.03] p-3">
        <img src={doctor.img} alt={doctor.name} className="h-10 w-10 rounded-full object-cover" />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{doctor.name}</div>
          <div className="truncate text-xs text-muted-foreground">{doctor.spec} · {visibleAppts.length} RDV</div>
        </div>
        <div className="ml-auto text-xs text-muted-foreground sm:hidden">{rangeLabel}</div>
      </div>

      {/* Grid */}
      <div className="mt-5">
        {vue === "Mois" ? (
          <MonthGrid
            cursor={cursor}
            appts={visibleAppts}
            onDropApt={move}
            onDragStartApt={onDragStart}
            hover={hover}
            setHover={setHover}
            allowDrop={allowDrop}
            onDrop={onDrop}
          />
        ) : (
          <TimeGrid
            days={vue === "Jour" ? [cursor] : Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(cursor), i))}
            appts={visibleAppts}
            onDragStartApt={onDragStart}
            hover={hover}
            allowDrop={allowDrop}
            onDrop={onDrop}
          />
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <button className="grid h-12 w-12 place-items-center rounded-full bg-foreground text-background shadow-[0_10px_30px_-10px_oklch(1_0_0/0.4)] hover:scale-105 transition">
          <Plus className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function ApptCard({ a, onDragStart }: { a: Apt; onDragStart: (e: DragEvent<HTMLDivElement>, a: Apt) => void }) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, a)}
      className={"group flex items-start gap-1.5 rounded-lg px-2 py-1.5 text-[11px] cursor-grab active:cursor-grabbing " + STATUS_TONE[a.status]}
      title={`${a.patient} · ${a.status} (${a.ref})`}
    >
      <GripVertical className="mt-0.5 h-3 w-3 shrink-0 opacity-50 group-hover:opacity-100" />
      <div className="min-w-0">
        <div className="truncate font-medium">{a.patient}</div>
        <div className="truncate opacity-80">{a.slot} · {a.ref}</div>
      </div>
    </div>
  );
}

function TimeGrid({
  days, appts, onDragStartApt, hover, allowDrop, onDrop,
}: {
  days: Date[];
  appts: Apt[];
  onDragStartApt: (e: DragEvent<HTMLDivElement>, a: Apt) => void;
  hover: string | null;
  allowDrop: (e: DragEvent<HTMLDivElement>, key: string) => void;
  onDrop: (e: DragEvent<HTMLDivElement>, date: string, slot: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <div
        className="grid min-w-[640px] gap-px rounded-xl bg-white/5 p-px"
        style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(0, 1fr))` }}
      >
        {/* Header row */}
        <div className="bg-background/40 p-2 text-[10px] uppercase tracking-wider text-muted-foreground">Heure</div>
        {days.map((d) => {
          const isToday = fmtISO(d) === fmtISO(new Date());
          return (
            <div key={fmtISO(d)} className="bg-background/40 p-2 text-center">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{DAY_LABELS[(d.getDay() + 6) % 7]}</div>
              <div className={"text-sm " + (isToday ? "font-semibold text-primary" : "text-foreground")}>{d.getDate()}</div>
            </div>
          );
        })}

        {/* Time rows */}
        {HOURS.map((h) => (
          <div key={h} className="contents">
            <div className="bg-background/40 p-2 text-right text-[10px] text-muted-foreground">{h}</div>
            {days.map((d) => {
              const iso = fmtISO(d);
              const key = `${iso}|${h}`;
              const items = appts.filter((a) => a.date === iso && a.slot === h);
              return (
                <div
                  key={key}
                  onDragOver={(e) => allowDrop(e, key)}
                  onDrop={(e) => onDrop(e, iso, h)}
                  className={
                    "min-h-[68px] space-y-1 bg-background/20 p-1.5 transition " +
                    (hover === key ? "bg-primary/10 ring-1 ring-primary/40" : "hover:bg-white/[0.03]")
                  }
                >
                  {items.map((a) => (
                    <ApptCard key={a.id} a={a} onDragStart={onDragStartApt} />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthGrid({
  cursor, appts, onDragStartApt, hover, allowDrop, onDrop,
}: {
  cursor: Date;
  appts: Apt[];
  onDropApt: (id: string, date: string, slot: string) => void;
  onDragStartApt: (e: DragEvent<HTMLDivElement>, a: Apt) => void;
  hover: string | null;
  setHover: (s: string | null) => void;
  allowDrop: (e: DragEvent<HTMLDivElement>, key: string) => void;
  onDrop: (e: DragEvent<HTMLDivElement>, date: string, slot: string) => void;
}) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-7 gap-px rounded-xl bg-white/5 p-px">
      {DAY_LABELS.map((d) => (
        <div key={d} className="bg-background/40 p-2 text-center text-[10px] uppercase tracking-wider text-muted-foreground">{d}</div>
      ))}
      {cells.map((d) => {
        const iso = fmtISO(d);
        const key = `${iso}|09:00`;
        const inMonth = d.getMonth() === cursor.getMonth();
        const isToday = iso === fmtISO(new Date());
        const items = appts.filter((a) => a.date === iso);
        return (
          <div
            key={iso}
            onDragOver={(e) => allowDrop(e, key)}
            onDrop={(e) => onDrop(e, iso, "09:00")}
            className={
              "min-h-[96px] space-y-1 p-1.5 transition " +
              (inMonth ? "bg-background/20" : "bg-background/10 opacity-60") +
              (hover === key ? " bg-primary/10 ring-1 ring-primary/40" : " hover:bg-white/[0.03]")
            }
          >
            <div className={"text-right text-[11px] " + (isToday ? "font-semibold text-primary" : "text-muted-foreground")}>{d.getDate()}</div>
            {items.slice(0, 3).map((a) => (
              <ApptCard key={a.id} a={a} onDragStart={onDragStartApt} />
            ))}
            {items.length > 3 && (
              <div className="text-[10px] text-muted-foreground">+{items.length - 3} autres</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
